import { useState, useEffect } from 'react';

interface WeatherData {
  temp: number;
  description: string;
  icon: string;
}

async function fetchWeather(lat: number, lon: number): Promise<WeatherData> {
  const res = await fetch(
    `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&timezone=auto`
  );
  if (!res.ok) throw new Error('Weather fetch failed');
  const data = await res.json();
  const code: number = data.current.weather_code;
  const temp: number = Math.round(data.current.temperature_2m);

  const description =
    code === 0 ? 'Clear sky' :
    code <= 3  ? 'Partly cloudy' :
    code <= 48 ? 'Foggy' :
    code <= 67 ? 'Rainy' :
    code <= 77 ? 'Snowy' :
    code <= 82 ? 'Showers' :
    'Stormy';

  const icon =
    code === 0 ? '☀️' :
    code <= 3  ? '⛅' :
    code <= 48 ? '🌫' :
    code <= 67 ? '🌧' :
    code <= 77 ? '❄️' :
    code <= 82 ? '🌦' :
    '⛈';

  return { temp, description, icon };
}

// Weather for the venue's saved location (set from its address).
// No device geolocation: it hangs inside the iOS app without a
// location permission, and the venue's weather is what matters anyway.
export function useWeather(lat?: number | null, lon?: number | null) {
  const [weather, setWeather] = useState<WeatherData | null>(null);

  useEffect(() => {
    if (lat == null || lon == null) { setWeather(null); return; }
    let cancelled = false;
    fetchWeather(lat, lon)
      .then((w) => { if (!cancelled) setWeather(w); })
      .catch(() => { if (!cancelled) setWeather(null); });
    return () => { cancelled = true; };
  }, [lat, lon]);

  return weather;
}